import type { UpdatePointMapDto } from '../types/dto';
import type {
  PointNote,
  PointNoteDetail,
  PointsStatsResponse,
  PointsValuesResponse,
} from '../types/points';
import type { MapPointScope } from '../utils/map-point-scope';

import { handleRequest, privateClient } from '@data-access';

const POINT_PATCH_TIMEOUT_MS = 90_000;

function pointScopeParams(scope?: MapPointScope) {
  return scope ? { searchParams: { scope } } : {};
}

export const pointsApi = {
  getStats: async () => {
    return handleRequest<PointsStatsResponse>(
      privateClient.get('points/stats/').json(),
    );
  },

  getPointsValues: async () => {
    return handleRequest<PointsValuesResponse>(
      privateClient.get('points/values/').json(),
    );
  },

  patchPoint: async (
    id: number,
    data: UpdatePointMapDto,
    scope?: MapPointScope,
  ) => {
    return handleRequest<unknown>(
      privateClient
        .patch(`points/${id}/`, {
          json: data,
          timeout: POINT_PATCH_TIMEOUT_MS,
          ...pointScopeParams(scope),
        })
        .json(),
    );
  },

  patchPointStoninessImage: async (args: {
    id: number;
    file: File;
    sachet_id?: string | null;
    stoniness?: string | null;
    is_sampled?: boolean;
    scope?: MapPointScope;
  }) => {
    const formData = new FormData();
    formData.append('stoniness_image', args.file);

    if (args.sachet_id !== undefined && args.sachet_id !== null) {
      formData.append('sachet_id', args.sachet_id);
    }

    if (args.stoniness !== undefined && args.stoniness !== null) {
      formData.append('stoniness', args.stoniness);
    }

    if (args.is_sampled !== undefined) {
      formData.append('is_sampled', String(args.is_sampled));
    }

    return handleRequest<unknown>(
      privateClient
        .patch(`points/${args.id}/`, {
          body: formData,
          timeout: POINT_PATCH_TIMEOUT_MS,
          ...pointScopeParams(args.scope),
        })
        .json(),
    );
  },

  patchPointSachet: async (args: {
    id: number;
    sachet_id: string;
    file: File;
    scope?: MapPointScope;
  }) => {
    const formData = new FormData();
    formData.append('sachet_id', args.sachet_id);
    formData.append('sachet_image', args.file);

    return handleRequest<unknown>(
      privateClient
        .patch(`points/${args.id}/`, {
          body: formData,
          timeout: POINT_PATCH_TIMEOUT_MS,
          ...pointScopeParams(args.scope),
        })
        .json(),
    );
  },

  patchPointImpossible: async (args: {
    id: number;
    title: string;
    content: string;
    image?: File | null;
    scope?: MapPointScope;
  }) => {
    const formData = new FormData();
    formData.append('is_impossible', 'true');
    formData.append('impossible_title', args.title);
    formData.append('impossible_content', args.content);
    formData.append('impossible_at', new Date().toISOString());
    if (args.image) {
      formData.append('impossible_image', args.image);
    }

    return handleRequest<unknown>(
      privateClient
        .patch(`points/${args.id}/`, {
          body: formData,
          timeout: POINT_PATCH_TIMEOUT_MS,
          ...pointScopeParams(args.scope),
        })
        .json(),
    );
  },

  getPointNotes: async (pointId: number) => {
    return handleRequest<PointNote[]>(
      privateClient.get(`points/${pointId}/notes/`).json(),
    );
  },

  getPointNoteDetail: async (pointId: number, noteId: number) => {
    return handleRequest<PointNoteDetail>(
      privateClient.get(`points/${pointId}/notes/${noteId}/`).json(),
    );
  },

  createPointNote: async (
    pointId: number,
    data: Pick<PointNote, 'title' | 'content'> & {
      images?: File[];
    },
  ) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('content', data.content);
    (data.images ?? []).forEach((file) => {
      formData.append('images', file);
    });

    return handleRequest<PointNoteDetail>(
      privateClient.post(`points/${pointId}/notes/`, { body: formData }).json(),
    );
  },

  patchPointNote: async (
    pointId: number,
    noteId: number,
    data: Partial<Pick<PointNote, 'title' | 'content'>> & {
      images?: File[];
      remove_image_ids?: number[];
    },
  ) => {
    const formData = new FormData();

    if (data.title !== undefined) {
      formData.append('title', data.title);
    }

    if (data.content !== undefined) {
      formData.append('content', data.content);
    }

    (data.images ?? []).forEach((file) => {
      formData.append('images', file);
    });

    (data.remove_image_ids ?? []).forEach((imageId) => {
      formData.append('remove_image_ids', String(imageId));
    });

    return handleRequest<PointNote>(
      privateClient.patch(`points/${pointId}/notes/${noteId}/`, { body: formData }).json(),
    );
  },

  deletePointNote: async (pointId: number, noteId: number) => {
    return handleRequest<string>(
      privateClient.delete(`points/${pointId}/notes/${noteId}/`).text(),
    );
  },
};

